import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import BrandMark from "../components/BrandMark";

const isRuEmail = (email) => {
  if (!email || !email.includes("@")) return false;
  const domain = email.split("@").pop().toLowerCase().replace(/\.$/, "");
  return domain.split(".").pop() === "ru";
};

export default function InviteRegister() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | valid | invalid
  const [errMsg, setErrMsg] = useState("");
  const [form, setForm] = useState({ email: "", password: "", confirm: "", name: "" });
  const [submitErr, setSubmitErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/api/invites/${token}`)
      .then((r) => {
        if (r.data.valid) setStatus("valid");
        else { setStatus("invalid"); setErrMsg(r.data.error || "Ссылка недействительна"); }
      })
      .catch((e) => { setStatus("invalid"); setErrMsg(formatApiError(e.response?.data?.detail) || e.message); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr("");
    if (!isRuEmail(form.email)) { setSubmitErr("Регистрация доступна только для адресов в зоне .ru"); return; }
    if (form.password.length < 8) { setSubmitErr("Пароль должен быть не короче 8 символов"); return; }
    if (form.password !== form.confirm) { setSubmitErr("Пароли не совпадают"); return; }
    setBusy(true);
    try {
      await api.post(`/api/invites/${token}/register`, {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name || undefined,
      });
      await refresh();
      nav("/admin", { replace: true });
    } catch (err) {
      setSubmitErr(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center label-mono" data-testid="invite-loading">Проверка приглашения…</div>;
  }
  if (status === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center space-y-4" data-testid="invite-invalid">
          <div className="label-mono text-destructive">ПРИГЛАШЕНИЕ НЕДОСТУПНО</div>
          <h1 className="text-3xl font-medium tracking-tight">{errMsg}</h1>
          <p className="text-foreground/60">Запросите новую ссылку у администратора Hop.Agency.</p>
          <Link to="/" className="hop-link inline-block mt-4">← на главную</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block bg-foreground text-background p-16">
        <div className="opacity-70"><BrandMark dark /></div>
        <h1 className="mt-12 text-5xl font-medium tracking-tight leading-tight">Регистрация<br/>в&nbsp;аналитическом контуре</h1>
        <p className="mt-8 text-lg opacity-70 max-w-md">
          Вы получили приглашение от Hop.Agency для просмотра обезличенных карточек стартапов и&nbsp;их&nbsp;питч-деков. Создайте аккаунт ниже.
        </p>
        <div className="mt-16 label-mono opacity-50">RESTRICTED · VIEWER</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="invite-register-form">
          <div>
            <Link to="/" className="hop-link text-sm">← на сайт</Link>
            <h2 className="text-3xl font-medium tracking-tight mt-3">Создать аккаунт</h2>
            <p className="text-sm text-foreground/60 mt-2">
              Регистрация доступна только для адресов в&nbsp;зоне <span className="mono">.ru</span>.
            </p>
          </div>
          <label className="block">
            <div className="label-mono mb-2">E-MAIL (только .ru)</div>
            <input data-testid="invite-email-input" className="field-input" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" required />
          </label>
          <label className="block">
            <div className="label-mono mb-2">ИМЯ (необязательно)</div>
            <input data-testid="invite-name-input" className="field-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} type="text" />
          </label>
          <label className="block">
            <div className="label-mono mb-2">ПАРОЛЬ (мин. 8 символов)</div>
            <input data-testid="invite-password-input" className="field-input" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} type="password" required />
          </label>
          <label className="block">
            <div className="label-mono mb-2">ПОВТОРИТЕ ПАРОЛЬ</div>
            <input data-testid="invite-confirm-input" className="field-input" value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} type="password" required />
          </label>
          {submitErr && <p className="text-destructive label-mono" data-testid="invite-error">{submitErr}</p>}
          <button type="submit" disabled={busy} className="btn-primary" data-testid="invite-submit-btn">
            {busy ? "Регистрируем…" : "Создать аккаунт и войти"}
          </button>
        </form>
      </div>
    </div>
  );
}
